export const CUSTOMER_DISPLAY_CHANNEL = "matjarpos-customer-display-v1";
export const CUSTOMER_DISPLAY_STORAGE_EVENT = `${CUSTOMER_DISPLAY_CHANNEL}:event`;
export const CUSTOMER_DISPLAY_STATE_CACHE = `${CUSTOMER_DISPLAY_CHANNEL}:state`;

export const DISPLAY_EVENTS = Object.freeze({
  CART_UPDATED: "CART_UPDATED",
  CART_CLEARED: "CART_CLEARED",
  PAYMENT_STARTED: "PAYMENT_STARTED",
  PAYMENT_UPDATED: "PAYMENT_UPDATED",
  PAYMENT_COMPLETED: "PAYMENT_COMPLETED",
  SALE_CANCELLED: "SALE_CANCELLED",
  DISPLAY_STATE_REQUESTED: "DISPLAY_STATE_REQUESTED",
  DISPLAY_STATE_RESPONSE: "DISPLAY_STATE_RESPONSE",
});

const ALLOWED_EVENTS = new Set(Object.values(DISPLAY_EVENTS));
const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const safeImageUrl = (value) => {
  const url = String(value || "").trim();
  return /^(https?:|data:image\/|\/)/i.test(url) ? url : "";
};

export const sanitizeDisplayState = (state = {}) => ({
  mode: ["WELCOME", "CART", "PAYMENT", "COMPLETED"].includes(state.mode) ? state.mode : "WELCOME",
  updatedAt: state.updatedAt || new Date().toISOString(),
  highlightedLineId: state.highlightedLineId == null ? null : String(state.highlightedLineId),
  shop: {
    name: String(state.shop?.name || "MATJARPOS"),
    logo: safeImageUrl(state.shop?.logo),
    address: String(state.shop?.address || ""),
    phone: String(state.shop?.phone || ""),
    email: String(state.shop?.email || ""),
  },
  display: {
    locale: String(state.display?.locale || "fr-MA"),
    currency: String(state.display?.currency || "MAD"),
    welcomeMessage: String(state.display?.welcomeMessage || "Bienvenue"),
    promotionImages: (state.display?.promotionImages || []).map(safeImageUrl).filter(Boolean).slice(0, 10),
    qrCode: safeImageUrl(state.display?.qrCode),
    confirmationSeconds: Math.max(1, Math.min(number(state.display?.confirmationSeconds) || 8, 60)),
  },
  lines: (state.lines || []).map((line) => ({
    id: String(line.id ?? ""),
    designation: String(line.designation || ""),
    quantity: number(line.quantity),
    unitPrice: number(line.unitPrice),
    lineTotal: number(line.lineTotal),
    discount: number(line.discount),
  })),
  totals: {
    subtotal: number(state.totals?.subtotal),
    discount: number(state.totals?.discount),
    finalAmount: number(state.totals?.finalAmount),
  },
  payment: {
    paidAmount: number(state.payment?.paidAmount),
    remainingAmount: number(state.payment?.remainingAmount),
    receivedAmount: number(state.payment?.receivedAmount),
    changeAmount: number(state.payment?.changeAmount),
  },
});

const parseEnvelope = (raw) => {
  try {
    const envelope = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!envelope || envelope.version !== 1 || !ALLOWED_EVENTS.has(envelope.type)) return null;
    return {
      version: 1,
      id: String(envelope.id || ""),
      type: envelope.type,
      timestamp: String(envelope.timestamp || ""),
      state: envelope.state ? sanitizeDisplayState(envelope.state) : undefined,
    };
  } catch {
    return null;
  }
};

export const readCachedDisplayState = (storage = window.localStorage) => {
  const envelope = parseEnvelope(storage.getItem(CUSTOMER_DISPLAY_STATE_CACHE));
  return envelope?.state || null;
};

export const createCustomerDisplayChannel = ({
  onMessage = () => {},
  targetWindow = window,
  BroadcastChannelImpl = targetWindow.BroadcastChannel,
} = {}) => {
  let channel = null;
  const seen = new Set();

  const deliver = (raw) => {
    const envelope = parseEnvelope(raw);
    if (!envelope || seen.has(envelope.id)) return;
    seen.add(envelope.id);
    onMessage(envelope);
  };

  if (typeof BroadcastChannelImpl === "function") {
    channel = new BroadcastChannelImpl(CUSTOMER_DISPLAY_CHANNEL);
    channel.onmessage = (event) => deliver(event.data);
  }

  const onStorage = (event) => {
    if (event.key === CUSTOMER_DISPLAY_STORAGE_EVENT && event.newValue) deliver(event.newValue);
  };
  targetWindow.addEventListener("storage", onStorage);

  const publish = (type, state) => {
    if (!ALLOWED_EVENTS.has(type)) throw new Error(`Unsupported customer-display event: ${type}`);
    const envelope = {
      version: 1,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type,
      timestamp: new Date().toISOString(),
      ...(state ? { state: sanitizeDisplayState(state) } : {}),
    };
    const serialized = JSON.stringify(envelope);
    if (channel) channel.postMessage(envelope);
    targetWindow.localStorage.setItem(CUSTOMER_DISPLAY_STORAGE_EVENT, serialized);
    if (envelope.state) targetWindow.localStorage.setItem(CUSTOMER_DISPLAY_STATE_CACHE, serialized);
    return envelope;
  };

  return {
    publish,
    close: () => {
      channel?.close();
      targetWindow.removeEventListener("storage", onStorage);
    },
    transport: channel ? "broadcast-channel" : "storage",
  };
};

